import { Day, DayOffsetTimeSlot } from "@clockwise/schema";
import { FlexibilityLearnMore } from "@clockwise/web-commons/src/ui/autopilot-flexibility/flexibility-learn-more";
import { DayOnOffMap } from "@clockwise/web-commons/src/ui/working-hours";
import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IOrg } from "../../__schema__";
import { useMonetization } from "../../hooks/useMonetization";
import { setPricingDialogOpen } from "../../state/actions/payments.actions";
import { IReduxState } from "../../state/reducers/root.reducer";
import { TrackingEvents, track } from "../../util/analytics.util";
import { EAutopilotWarning, EFlex } from "../../util/event-tag.util";
import { AutopilotFlexibility } from "../autopilot-flexibility";

const DEFAULT_FLEX = EFlex.Day;

type FlexFormState = {
  flex: EFlex;
  allowSimilarRooms: boolean;
  timeRange?: {
    allowedStart: DayOffsetTimeSlot | null;
    allowedEnd: DayOffsetTimeSlot | null;
  };
  daysOfWeek?: Day[];
};

type Props = {
  /** @deprecated Provided by Relay, remove after migrating child components to Apollo */
  org: IOrg;
  showRoom: boolean;
  /**
   * ISO Formatted Date string: `YYYY-MM-DD`
   */
  selectedDate: string;
  disabled?: boolean;
  warning?: EAutopilotWarning;
  dayOnOffMap: DayOnOffMap;
  onChange: (state: FlexFormState) => void;
} & FlexFormState;

export function AutopilotFormSidebarScheduler({
  org,
  flex,
  showRoom,
  disabled = false,
  warning,
  allowSimilarRooms,
  timeRange,
  daysOfWeek,
  selectedDate,
  dayOnOffMap,
  onChange,
}: Props) {
  const dispatch = useDispatch();
  const billingGroupIds = useSelector((state: IReduxState) => state.payments.orgBillingGroupIds);
  const hasBillingGroup = billingGroupIds.length > 0;
  const { hasPaidPlan } = useSelector((state: IReduxState) => state.featureGrid);
  const shouldShowJoinExistingPlanToUser = hasBillingGroup && hasPaidPlan;
  const {
    autopilotWhenOrganizerCurrentlyAllowed: autopilotAllowed,
    moveRangesEnabled: isPlanUnrestricted,
  } = useSelector((state: IReduxState) => state.featureGrid.freePaidFeatures);
  const { trialExpirationStatus, canUserAccessFlexMeetings } = useMonetization();
  const isToggledOn =
    flex !== EFlex.Pin && (!trialExpirationStatus.isTrialExpired || canUserAccessFlexMeetings);
  const formState = useMemo<FlexFormState>(
    () => ({
      flex,
      allowSimilarRooms,
      timeRange,
      daysOfWeek,
    }),
    [allowSimilarRooms, daysOfWeek, flex, timeRange],
  );

  const onToggleFlex = () => {
    // important!!
    // if currently on, toggling turns autopilot off, which pins it
    const flex = isToggledOn ? EFlex.Pin : DEFAULT_FLEX;
    onChange({
      ...formState,
      flex,
    });
  };
  const onToggleRoom = () => {
    onChange({
      ...formState,
      allowSimilarRooms: !formState.allowSimilarRooms,
    });
  };
  const onChangeFlex = (flex: EFlex, daysOfWeek: Day[]) => {
    onChange({
      ...formState,
      flex,
      daysOfWeek,
    });
  };
  const onChangeTime = (
    allowedStart: DayOffsetTimeSlot | null,
    allowedEnd: DayOffsetTimeSlot | null,
  ) => {
    onChange({
      ...formState,
      timeRange: {
        allowedStart,
        allowedEnd,
      },
    });
  };

  const onClickViewPlans = () => {
    dispatch(setPricingDialogOpen(true));
    track(TrackingEvents.PAYWALLS.MOVE_RANGES_CLICK_VIEW_PLANS);
  };
  const onFlexibilityFaqSelect = () => track(TrackingEvents.HELP.WHAT_MEETINGS_SHOULD_MARK_FLEX);

  const currentFlex = formState.flex !== EFlex.Pin ? formState.flex : DEFAULT_FLEX;

  return (
    <div className="cw-mt-1 cw-pl-1 cw-pr-2">
      <AutopilotFlexibility
        event={null} /* Provided by Relay */
        org={org ?? null} /* Provided by Relay */
        trialExpirationStatus={trialExpirationStatus}
        onChangeFlex={onChangeFlex}
        onChangeRoom={onToggleRoom}
        onChangeTime={onChangeTime}
        selectedFlex={currentFlex}
        allowSimilarRooms={!!formState.allowSimilarRooms}
        disabled={disabled || !!warning}
        showRoom={showRoom}
        selectedDate={selectedDate}
        isAutopilotEnabled={isToggledOn}
        planRestricted={!isPlanUnrestricted}
        onClickViewPlans={onClickViewPlans}
        allowedStart={formState.timeRange?.allowedStart}
        allowedEnd={formState.timeRange?.allowedEnd}
        schedulingNewEvent
        attendeeMeetingHoursIntersect={true}
        onToggle={onToggleFlex}
        warning={warning}
        fixed={!autopilotAllowed}
        isExtension={false}
        showDayOfWeekToggle={true}
        dayOnOffMap={dayOnOffMap}
        surface="Sidepanel"
        showJoinExistingPlan={shouldShowJoinExistingPlanToUser}
        canUserAccessFlexMeetings={canUserAccessFlexMeetings}
      />
      {isToggledOn && (
        <div className="cw-mt-2">
          <FlexibilityLearnMore onFlexibilityFaqSelect={onFlexibilityFaqSelect} />
        </div>
      )}
    </div>
  );
}
